import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

import type {
  ButtonBaseProps,
  InnerButtonProps,
  ToggleWrapperProps,
} from '@bonniernews/dn-design-system-web/components/button/button-types.ts'
import { IconUse } from '@bonniernews/dn-design-system-web/components/icon-sprite/icon-sprite.tsx'
import { Spinner } from '@bonniernews/dn-design-system-web/components/spinner/spinner.tsx'
import type { SpinnerProps } from '@bonniernews/dn-design-system-web/components/spinner/spinner.tsx'

export const InnerButton = ({ text, isIconButton = false, attributes, icon, loadingHtml }: InnerButtonProps) => {
  let optionalHtml
  if (!isIconButton && attributes['aria-label']) {
    optionalHtml = <span aria-hidden='true'>{text}</span>
  } else if (!isIconButton) {
    optionalHtml = <span>{text}</span>
  }

  return (
    <>
      {optionalHtml}
      {icon}
      {loadingHtml}
    </>
  )
}

export const ToggleWrapper = ({
  selected = false,
  disabled = false,
  attributes,
  classes,
  onChild,
  offChild,
  loadingHtml,
}: ToggleWrapperProps) => {
  return (
    <button type='button' role='switch' aria-checked={selected} className={classes} {...attributes} disabled={disabled}>
      <span className='ds-btn__off' aria-hidden='true'>
        {onChild}
      </span>
      <span className='ds-btn__on' aria-hidden='true'>
        {offChild}
      </span>
      {loadingHtml}
    </button>
  )
}

export const ButtonBase = ({
  text,
  disabled = false,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  mobileFullWidth = false,
  iconPosition = 'none',
  iconName,
  loading = false,
  type = 'button',
  href,
  classNames,
  attributes = {},
  forcePx = false,
  isIconButton = false,
  selected = false,
  isToggle = false,
  selectedIconName,
  selectedText,
}: ButtonBaseProps) => {
  if (isIconButton && text && !attributes['aria-label']) {
    attributes['aria-label'] = text
  } else if (isToggle && text && selectedText && !attributes['aria-label']) {
    attributes['aria-label'] = selected ? selectedText : text
  }
  let spinnerVariant: SpinnerProps['variant'] = 'secondary'
  if (variant === 'staticWhite' || variant === 'transparent') {
    spinnerVariant = 'staticBlack'
  } else if (variant === 'secondaryFilled' || variant === 'secondaryOutline') {
    spinnerVariant = 'primary'
  }
  const loadingHtml = (
    <Spinner {...{ size: 'small', variant: spinnerVariant, forcePx, attributes: { 'aria-hidden': 'true' } }} />
  )

  let icon
  if (iconName && (iconPosition != 'none' || isIconButton == true)) {
    icon = <IconUse {...{ iconName, attributes: { 'aria-hidden': 'true' } }} />
  }

  const classNamePrefix = 'ds-btn--'
  const classes = formatClassString([
    'ds-btn',
    `${classNamePrefix}${variant}`,
    `${classNamePrefix}${size}`,
    isToggle && selected && `${classNamePrefix}selected`,
    fullWidth && `${classNamePrefix}full-width`,
    mobileFullWidth && `${classNamePrefix}mobile-full-width`,
    icon && !isIconButton && `${classNamePrefix}icon-${iconPosition}`,
    isIconButton && `${classNamePrefix}icon-only`,
    isToggle && `${classNamePrefix}toggle`,
    loading && 'ds-loading',
    forcePx && 'ds-force-px',
    classNames,
  ])

  let buttonToRender
  if (isToggle) {
    let onChild, offChild
    if (isIconButton && iconName && selectedIconName) {
      onChild = <IconUse iconName={iconName} />
      offChild = <IconUse iconName={selectedIconName} />
    } else {
      onChild = <span>{text}</span>
      offChild = (
        <>
          <IconUse iconName='check' />
          <span>{selectedText}</span>
        </>
      )
    }
    buttonToRender = <ToggleWrapper {...{ selected, classes, attributes, disabled, onChild, offChild, loadingHtml }} />
  } else {
    const buttonParams = { isIconButton, text, icon, loadingHtml, attributes }
    if (href) {
      // Don't render spinner when button is a link
      const params = { ...buttonParams, loadingHtml: null }
      buttonToRender = (
        <a href={href} className={classes} {...attributes}>
          <InnerButton {...params} />
        </a>
      )
    } else {
      buttonToRender = (
        <button type={type} className={classes} {...attributes} disabled={disabled}>
          <InnerButton {...buttonParams} />
        </button>
      )
    }
  }

  return <>{buttonToRender}</>
}
